import { acceptHMRUpdate, defineStore } from 'pinia'
import { useMaps } from './maps'
import { toRaw } from 'vue'
import { usePlanner } from './planner'
import { useCustomers } from './customers'
import { useFieldLayersStore } from './fieldLayers'
import { 
    postGenerateFieldConnection, 
    postSaveFieldConnection,
    getFieldROIs,
    getFarmROIs,
    deleteOldFieldROI,
    postExtractGeometryFromShape,
    getFieldCutoutHeadlandSegments
} from '@/scripts/fieldsPlanning'
import { formatDate } from '../helpers/dateHelper'
import { useRoiStore } from './roi'
import i18n from '@/i18n'
import useModal from '@/stores/modal'
import * as turf from '@turf/turf'
import { getClosestPointPolyline, geoJsonRingToOpenPath } from '@/helpers/geometryHelper'
import { ROAD_LINE_LENGTH_METERS } from '@/scripts/util/constants'
import moment from 'moment'
import { downloadFeatureAsShapefile } from '@/scripts/shapefileExport'
import { v4 as uuid } from 'uuid'

// Road line length constant (in meters) with a bit of security distance
const ROAD_LINE_LENGTH_METERS_WITH_OFFSET = ROAD_LINE_LENGTH_METERS + ROAD_LINE_LENGTH_METERS * 0.2 / 15

export const useFieldsConnectionStore = defineStore('fieldsConnection', {
    state: () => ({
        startField_id: null,
        endField_id: null,
        loading: false,
        redirectLoading: false,
        errorConnGen: false,
        errorMessage: {
            reason: '',
            info: '',
        },
        geoRequestData: null,
        lines: [], // drawed lines
        currentLine: {
            coordinates: [],
            polyline: null
        },
        drawingEnabled: false,
        connectionMode: 'single', // 'single' or 'double'
        totalLines: 2, // 2 lines for single mode, 3 lines for double mode
        completedLines: 0, // number of lines completed (already drawed)
        isDrawingStart: true, // Flag to know if we are drawing the start or end point
        routeGenerated: false,
        isLineChanged: false,
        warning: {
            type: '',
            message: '',
        },
        selectedCustomer: null,
        clickListener: null,
        mouseMoveListener: null,
        fieldListeners: [], // Array for all field listeners
        lineListeners: [], // Array for polyline and map listeners from setLineClickable
        documentListeners: [], // Array for document event listeners
        generatedROIs: [],
        roisByFieldId: {},
        viewROIsData: {}, // ROIs from other fields (for viewing)
        steps: 1,
        openInfoWindows: [],
        roiMapClickListener: null,
        geojsonResponse: null,
        generatedRoute: null,
        otherFieldsVisible: false,
        forceMiddle: false,
        forceRoadLineLength: false
    }),
    
    actions: {
        nextStep() {
            this.steps++
        },
        setCustomer() {
            const customersStore = useCustomers()

            if (customersStore.customers.length === 1) {
                this.selectedCustomer = customersStore.customers[0]
            } else if (customersStore.selectedCustomers.length === 1) {
                this.selectedCustomer = customersStore.selectedCustomers[0]
            } else {
                this.selectedCustomer = null
            }
        },
        getGenerateLabel() {
            return this.routeGenerated ? 'Regenerate' : 'Generate'
        },
        async setConnectionMode(mode) {
            this.connectionMode = mode
            await this.resetDrawingState(true)
            
            // Set the number of lines depending on the mode
            this.totalLines = mode === 'single' ? 2 : 3
        },
        validateDrawingState() {
            if (this.completedLines >= this.totalLines) {
                return false
            }

            if (this.connectionMode === 'single' && !this.startField_id) {
                return false
            }
            if (this.connectionMode === 'double' && (!this.startField_id || !this.endField_id)) {
                return false
            }

            return true
        },
        initNewLine() {
            const mapStore = useMaps()
            
            this.currentLine = {
                coordinates: [],
                polyline: new mapStore.google.maps.Polyline({
                    path: [],
                    strokeColor: '#FF0000',
                    strokeOpacity: 1.0,
                    strokeWeight: 3,
                    map: toRaw(mapStore.map),
                    clickable: false,
                    zIndex: 1001
                })
            }
        },
        configLineArrowsAndFields() {
            if (this.completedLines % 2 === 0) {
                this.handleFieldClickable(false)

                const ps = usePlanner()
                ps.addDirectionalArrows(
                    [this.currentLine.polyline],
                    '#FF0000', // fillColor - red for arrows
                    '#000000'  // strokeColor - black for contour
                )

                this.animateCircle(this.currentLine.polyline)
            } else {
                this.handleFieldClickable(true)
            }
        },
        handleFieldClickable(clickable) {
            const mapStore = useMaps()

            mapStore.draw.fields.forEach(field => {
                field.poly.setOptions({ clickable: clickable })
            })
            
            mapStore.draw.field.headlanes.forEach(headland => {
                if (headland) {
                    headland.setOptions({ clickable: clickable })
                }
            })

            mapStore.draw.field.segments.forEach(segment => {
                if (segment) {
                    segment.setOptions({ clickable: clickable })
                }
            })
        },
        animateCircle(line, interval = 30) {
            let count = 0
            
            const intervalId = setInterval(() => {
                count = (count + 1) % 1000
                const polyline = toRaw(line)
                const icons = polyline.get("icons")
                icons[0].offset = (count / 10) + "%"
                polyline.set("icons", icons)
            }, interval)

            toRaw(line).animationInterval = intervalId
        },
        handleLineCompletion(event) {
            const mapStore = useMaps()
            this.currentLine.coordinates.push(event.latLng)
            
            const start = this.currentLine.coordinates[0]
            const end = this.currentLine.coordinates[1]
            
            // Check for same points with tolerance
            const tolerance = 0.000001 // Very small tolerance for coordinate comparison
            if (Math.abs(start.lat() - end.lat()) < tolerance && 
                Math.abs(start.lng() - end.lng()) < tolerance) {
                return false
            }
            let snapped_start, snapped_end
            if (this.lines.length !== 1) {
                //Snap the line to the nearest points either on an ab-line or headland 
                const trackSystem = mapStore.draw.field.abLines.concat(mapStore.draw.field.headlanes)          
                snapped_start = getClosestPointPolyline({lat: start.lat(), lng: start.lng()}, trackSystem)
                snapped_end = getClosestPointPolyline({lat: end.lat(), lng: end.lng()}, trackSystem)
                snapped_start  = new mapStore.google.maps.LatLng(snapped_start.lat, snapped_start.lng)
                snapped_end = new mapStore.google.maps.LatLng(snapped_end.lat, snapped_end.lng)
            } else {
                snapped_start = start
                snapped_end = end
            }
            this.currentLine.polyline.setPath([snapped_start, snapped_end])
            this.lines.push({
                coordinates: [...this.currentLine.coordinates],
                polyline: this.currentLine.polyline
            })
            
            // Enforce 15m + 0.2m (ROAD_LINE_LENGTH_METERS_WITH_OFFSET) length for road line (index 1)
            if (this.lines.length === 2 && this.forceRoadLineLength) {
                this.enforceRoadLineLength(this.lines[1])
            }
            
            this.completedLines++

            mapStore.google.maps.event.removeListener(this.clickListener)
            if (this.mouseMoveListener) {
                mapStore.google.maps.event.removeListener(this.mouseMoveListener)
            }
            
            mapStore.map.setOptions({ draggableCursor: null })
            this.drawingEnabled = false

            return true
        },
        setupEventListeners() {
            const mapStore = useMaps()

            // Remove existing listeners if they exist
            if (this.clickListener) {
                mapStore.google.maps.event.removeListener(this.clickListener)
            }
            if (this.mouseMoveListener) {
                mapStore.google.maps.event.removeListener(this.mouseMoveListener)
            }

            this.clickListener = mapStore.google.maps.event.addListener(mapStore.map, 'click', (event) => {
                if (!this.drawingEnabled) return

                if (this.isDrawingStart) {
                    // Start drawing a new line
                    this.currentLine.coordinates = [event.latLng]
                    this.isDrawingStart = false
                } else {
                    // Complete the current line
                    const completed = this.handleLineCompletion(event)
                    if (completed) {
                        if (this.completedLines < this.totalLines) {
                            // line completed/closed, start drawing the next line
                            this.startDrawingLine()
                        } else {
                            // all lines have been drawn/closed
                            this.finishDrawing()
                        }
                    }
                }
            })

            this.mouseMoveListener = mapStore.google.maps.event.addListener(mapStore.map, 'mousemove', (event) => {
                if (!this.drawingEnabled || this.isDrawingStart) return
                const previewPath = [this.currentLine.coordinates[0], event.latLng]
                this.currentLine.polyline.setPath(previewPath)
            })
            
        },
        startDrawingLine() {
            const roi = useRoiStore()
            const isDrawingObstacles = roi.roisType === 'obstaclesROIs'

            if (!this.validateDrawingState() || isDrawingObstacles) return

            const mapStore = useMaps()
            
            // Make ROIs non-clickable when starting to draw
            this.setROIsClickable(false)
            
            this.initNewLine()
            this.configLineArrowsAndFields()
            
            this.drawingEnabled = true
            this.isDrawingStart = true

            mapStore.map.setOptions({ draggableCursor: 'crosshair' })

            this.setupEventListeners()
        },
        setLineClickable(line, lineIndex) {
            line.polyline.setOptions({ clickable: true, editable: true, draggable: false })

            // Store initial coordinates
            const initialPath = line.polyline.getPath()
            const initialStart = initialPath.getAt(0)
            const initialEnd = initialPath.getAt(1)
            
            // For road line (index 1), handle manual dragging
            if (lineIndex === 1) {
                this.handleRoadLineDrag(line, initialStart, initialEnd)
            } else {
                this.handleFieldLineSnap(line, initialStart, initialEnd)
            }
        },
        finishDrawing() {
            const mapStore = useMaps()

            this.lines.forEach((line, index) => {
                this.setLineClickable(line, index)
            })
            
            this.drawingEnabled = false
            mapStore.map.setOptions({ draggableCursor: null })

            mapStore.draw.fields.forEach(field => {
                if (field.poly) {
                    field.poly.setOptions({ clickable: true })
                }
            })

            // Make ROIs clickable again when stopping drawing
            this.setROIsClickable(true)
        },
        setFieldStyle(field, isSelected = false) {
            if (field.poly) {
                toRaw(field.poly).setOptions({
                    fillColor: isSelected ? field.color : '#808080',
                    editable: false,
                    fillOpacity: 0.5,
                    strokeColor: isSelected ? field.color : '#000000',
                    strokeOpacity: 1,
                    strokeWeight: 2
                })
            }
        },
        setFieldsColor() {
            const mapStore = useMaps()
            mapStore.fields.forEach(field => {
                this.setFieldStyle(field, true)
            })
        },
        async redisplayAllFieldsColored() {
            const mapStore = useMaps()
            if (mapStore.fields.some(field => !field.poly)) {
                mapStore.drawFields()
            }
            await mapStore.showAllBorders()
            this.setFieldsColor()
        },
        // It stores the current index of the geometries to know on which field make the changes (toggle double mode)
        drawField(field) {
            const mapStore = useMaps()

            // 1. Save the length of geometries BEFORE drawing
            const headlanesStartIndex = mapStore.draw.field.headlanes.length
            const abLinesStartIndex = mapStore.draw.field.abLines.length
            const segmentsStartIndex = mapStore.draw.field.segments.length
            const trackWheelsStartIndex = mapStore.draw.field.trackWheels.length

            mapStore.draw.fields.push(field)
            
            // Show the field polygon on the map
            if (field.poly) {
                toRaw(field.poly).setMap(toRaw(mapStore.map))
                toRaw(field.poly).setVisible(true)
            }
            
            // 2. Draw geometries
            if (field.headland) mapStore.drawHeadLands(field)
            if (field.ab_lines) mapStore.drawABLines(field)
            if (field.headland_segments) mapStore.drawHeadlandSegments(field)
            if (field.ab_track_lines) mapStore.drawTrackWheels(field)

            // 3. Store indices (used in getGeometryRange() in fieldLayers.js)
            field.drawnGeometries = {
                headlanes: { start: headlanesStartIndex, end: mapStore.draw.field.headlanes.length },
                abLines: { start: abLinesStartIndex, end: mapStore.draw.field.abLines.length },
                segments: { start: segmentsStartIndex, end: mapStore.draw.field.segments.length },
                trackWheels: { start: trackWheelsStartIndex, end: mapStore.draw.field.trackWheels.length }
            }
        },
        async drawSelectedFields() {
            const mapStore = useMaps()

            // Clear the previously drawn elements
            mapStore.hideNavField()
            
            // Clear the fields array to avoid duplicates
            mapStore.draw.fields.forEach(field => {
                if (field.poly) {
                    toRaw(field.poly).setMap(null)
                }
            })
            mapStore.draw.fields = []

            try {
                this.loading = true
                
                // Reset drawing state completely
                this.clearLines()
                
                // Draw the first field if it exists
                if (this.startField_id) {
                    const startField = await mapStore.getField(this.startField_id)
                    if (!startField || !startField.ab_lines || !startField.headland) {
                        this.warning.type = 'error'
                        this.warning.message = startField ? 'Field is not valid' : 'Field not found'
                        await this.resetField(this.startField_id)
                        return
                    }
                    this.drawField(startField)
                    await this.getFieldROIs(this.startField_id, true)
                }

                // Draw the second field if it exists and we are in double mode
                if (this.connectionMode === 'double' && this.endField_id) {
                    const endField = await mapStore.getField(this.endField_id)
                    if (!endField || !endField.ab_lines || !endField.headland) {
                        this.warning.type = 'error'
                        this.warning.message = endField ? 'Field is not valid' : 'Field not found'
                        await this.resetField(this.endField_id)
                        return
                    }
                    this.drawField(endField)
                    await this.getFieldROIs(this.endField_id, true)
                }
            } catch (error) {
                console.error('Error drawing the fields:', error)
            } finally {
                this.loading = false
            }
        },
        async onStartFieldChange(fieldId) {
            const mapStore = useMaps()
            const fls = useFieldLayersStore()
            const field = mapStore.fields.find(f => f.id === fieldId)
            if (field) {
                // Same field as end: drop end silently (lists already exclude duplicates)
                if (field.id === this.endField_id) {
                    this.endField_id = null
                }
                
                // Reset old field if exists (when changing from dropdown)
                const oldField = mapStore.draw.fields.find(f => f.id !== fieldId && f.id !== this.endField_id)
                if (oldField) {
                    this.resetOldField(oldField.id)
                }
                
                // Clear previous lines and drawing state
                this.clearLines()
                
                mapStore.centerToField(field)
                this.setFieldStyle(field, true)
                
                const roi = useRoiStore()
                const isObstaclesMode = this.connectionMode === 'single' && roi.roisType === 'obstaclesROIs'
                
                if (isObstaclesMode) {
                    // Obstacles mode: clear view ROIs and listeners before redrawing
                    this.clearViewROIs()
                    this.clearFieldListeners()
                } else if (this.connectionMode === 'single') {
                    mapStore.hideAllBorders()
                    mapStore.showField(fieldId, false)
                } else if (this.endField_id) {
                    // Double mode - hide borders only when both fields are selected
                    mapStore.hideAllBorders()
                    mapStore.showField(fieldId, false)
                    mapStore.showField(this.endField_id, false)
                }
                await this.drawSelectedFields()
                
                // Obstacles mode: restore other fields and add view ROI listeners
                // Skip if startField_id is null (field was invalid and reset by resetField in call of drawSelectedFields)
                // In obstacles mode, show other fields with ROI toggle listeners
                if (isObstaclesMode && this.startField_id !== null) {
                    this.showOtherFields()
                }
                
                // Initialize the layers after drawing
                if (this.connectionMode === 'double') {
                    fls.initializeField1Layers()
                } else {
                    // Single mode
                    fls.initializeSingleFieldLayers()
                }
            }
        },
        async onEndFieldChange(fieldId) {
            const mapStore = useMaps()
            const fls = useFieldLayersStore()
            const field = mapStore.fields.find(f => f.id === fieldId)
            if (field) {
                if (field.id === this.startField_id) {
                    this.endField_id = null
                    return
                }
                
                // Reset old end field if exists (when changing from dropdown)
                const oldField = mapStore.draw.fields.find(f => f.id !== fieldId && f.id !== this.startField_id)
                if (oldField) {
                    this.resetOldField(oldField.id)
                }
                
                // Clear previous lines and drawing state
                this.clearLines()
                
                mapStore.centerToField(field)
                this.setFieldStyle(field, true)
                
                // Hide all fields
                mapStore.hideAllBorders()
                
                // Show only the selected fields
                mapStore.showField(this.startField_id, false)
                mapStore.showField(fieldId, false)
                
                await this.drawSelectedFields()
                
                // Reinitialize the layers for both fields after drawing
                fls.initializeField1Layers()
                fls.initializeField2Layers()
            }
        },
        clearLines() {
            this.clearDrawnLines()
            this.clearGeneratedROIs()
            this.clearErrorMessage()
            this.clearGeneratedRoutes()
            this.stopDrawingListeners()
        },
        // Hide field layers and remove from draw.fields
        hideFieldLayers(fieldId) {
            const mapStore = useMaps()
            const drawnField = mapStore.draw.fields.find(f => f.id === fieldId)
            if (drawnField?.drawnGeometries) {
                const geo = drawnField.drawnGeometries
                for (let i = geo.headlanes.start; i < geo.headlanes.end; i++) {
                    toRaw(mapStore.draw.field.headlanes[i])?.setMap(null)
                }
                for (let i = geo.abLines.start; i < geo.abLines.end; i++) {
                    toRaw(mapStore.draw.field.abLines[i])?.setMap(null)
                }
                for (let i = geo.segments.start; i < geo.segments.end; i++) {
                    toRaw(mapStore.draw.field.segments[i])?.setMap(null)
                }
                if (geo.trackWheels) {
                    for (let i = geo.trackWheels.start; i < geo.trackWheels.end; i++) {
                        toRaw(mapStore.draw.field.trackWheels[i])?.setMap(null)
                    }
                }
            }
            mapStore.draw.fields = mapStore.draw.fields.filter(f => f.id !== fieldId)
        },
        // Reset old field when changing from dropdown (no listener, no showAllBorders)
        resetOldField(fieldId) {
            if (fieldId == null || fieldId === '') return
            const mapStore = useMaps()
            this.hideFieldLayers(fieldId)
            const field = mapStore.fields.find(f => f.id === fieldId)
            if (field?.poly) {
                toRaw(field.poly).setOptions({ clickable: true })
                this.setFieldStyle(field, false)
            }
            this.hideOtherFields()
        },
        // Reset one field - full reset with listener
        async resetField(fieldId) {
            if (fieldId == null || fieldId === '') return
            const mapStore = useMaps()
            const fls = useFieldLayersStore()
            
            const remainingFieldId = fieldId === this.startField_id ? this.endField_id : this.startField_id
            
            // Reset field id
            if (fieldId === this.startField_id) {
                this.startField_id = null
            } else {
                this.endField_id = null
            }
            
            // Clear ROI listeners and view ROIs (for all modes, in case showOtherFields was used)
            this.clearFieldListeners()
            this.clearViewROIs()
            this.otherFieldsVisible = false
            
            this.resetOldField(fieldId)
            this.clearLines()

            // Reset field layers
            if (this.connectionMode === 'single') {
                fls.resetSingleFieldLayers()
            } else {
                fls.resetFieldConnectionLayers()
            }
            
            await mapStore.showAllBorders(remainingFieldId)
            
            // Pass fieldId (reset field) so only it becomes gray; remaining field keeps its color
            this.addFieldListeners(fieldId)
        },
        // Reset drawing (without ROIs) and start drawing again
        resetDrawing() {
            this.clearDrawnLines()
            this.clearGeneratedRoutes()
            this.clearErrorMessage()
            this.stopDrawingListeners()
        },
        async resetDrawingState(addListeners) {
            const mapStore = useMaps()
            const fls = useFieldLayersStore()
            const roi = useRoiStore()

            // Clear all ROIs
            roi.clearAllElements()

            // Stop TerraDraw if it's enabled to prevent conflicts
            if (mapStore.terraDraw && mapStore.terraDraw.enabled) {
                mapStore.terraDraw.setMode('select')
                mapStore.terraDraw.stop()
            }

            mapStore.draw.fields = []
            
            // Reset field layers for single and double mode
            fls.resetSingleFieldLayers()
            fls.resetFieldConnectionLayers()
            
            // Clear viewed ROIs from other fields
            this.clearViewROIs()

            // Clear all lines
            this.clearLines()

            // Reset additional data in the store
            this.warning.type = ''
            this.warning.message = ''
            this.loading = false
            this.hideOtherFields()

            // Clear all field listeners
            this.fieldListeners.forEach(listener => {
                mapStore.google.maps.event.removeListener(listener)
            })
            this.fieldListeners = []

            // Reset the data in the store
            this.startField_id = null
            this.endField_id = null

            // Clear field layers (AB lines, headlanes, track wheels, etc.)
            mapStore.hideNavField()

            if (addListeners) {

                // Ensure all fields have a poly drawn
                if (mapStore.fields.some(field => !field.poly)) {
                    mapStore.drawFields()
                }
                // Reset all fields to gray BEFORE showing them
                mapStore.fields.forEach(field => {
                    if (field.poly) {
                        this.setFieldStyle(field, false)
                        field.poly.setOptions({ clickable: true })
                    }
                })

                // Show all fields
                await mapStore.showAllBorders()

                // Ensure fields stay gray after showing (in case showAllBorders modifies them)
                mapStore.fields.forEach(field => {
                    if (field.poly) {
                        this.setFieldStyle(field, false)
                    }
                })

                this.addFieldListeners()
            }
        },
        clearGeneratedRoutes() {
            if (this.generatedRoute) {
                this.generatedRoute.forEach(poly => {
                    toRaw(poly).setMap(null)
                })
                this.generatedRoute = []
            }
        },
        clearDrawnLines() {
            // Clear the drawn lines
            this.lines.forEach(line => {
                toRaw(line.polyline).setMap(null)
            })
            this.lines = []
            
            // Clear the current line
            if (this.currentLine.polyline) {
                toRaw(this.currentLine.polyline).setMap(null)
            }
            this.currentLine = {
                coordinates: [],
                polyline: null
            }
            this.completedLines = 0
            this.routeGenerated = false
        },
        clearGeneratedROIs() {
            if (this.generatedROIs) {
                this.generatedROIs.forEach(poly => {
                    toRaw(poly).setMap(null)
                })
                this.generatedROIs = []
            }
        },
        clearErrorMessage() {
            this.errorConnGen = false
            this.errorMessage.reason = ''
            this.errorMessage.info = ''
            this.geoRequestData = null
        },
        // Set clickable/non-clickable the ROIs to allow drawing or dragging a field connection a line
        setROIsClickable(clickable) {
            // Set clickable for ROIs by field ID (the fields that are being worked on)
            if (this.roisByFieldId) {
                Object.values(this.roisByFieldId).flat().forEach(roi => {
                    const rawROI = toRaw(roi)
                    if (rawROI.setOptions) {
                        if (rawROI.setPath) {
                            rawROI.setOptions({ clickable: clickable })
                        } else {
                            rawROI.setOptions({ gmpClickable: clickable })
                        }
                    }
                })
            }
            
            // Set clickable for viewed ROIs from other fields
            if (this.viewROIsData) {
                Object.values(this.viewROIsData).flat().forEach(roi => {
                    const rawROI = toRaw(roi)
                    if (rawROI.setOptions) {
                        if (rawROI.setPath) {
                            rawROI.setOptions({ clickable: clickable })
                        } else {
                            rawROI.setOptions({ gmpClickable: clickable })
                        }
                    }
                })
            }
        },
        stopDrawingListeners() {
            const mapStore = useMaps()
            
            // Clear current line if it exists (started but not finished)
            if (this.currentLine.polyline) {
                toRaw(this.currentLine.polyline).setMap(null)
            }
            this.currentLine = {
                coordinates: [],
                polyline: null
            }
            
            // Reset drawing state
            this.isLineChanged = false
            this.drawingEnabled = false
            this.isDrawingStart = true

            // Clear listeners for click
            if (this.clickListener) {
                mapStore.google.maps.event.removeListener(this.clickListener)
            }
            // Clear listeners for mousemove
            if (this.mouseMoveListener) {
                mapStore.google.maps.event.removeListener(this.mouseMoveListener)
            }
            
            // Clear line-related listeners (from setLineClickable)
            this.lineListeners.forEach(listener => {
                mapStore.google.maps.event.removeListener(listener)
            })
            this.lineListeners = []
            
            // Clear document event listeners
            this.documentListeners.forEach(handler => {
                document.removeEventListener('mouseup', handler)
            })
            this.documentListeners = []

            // Reset the cursor
            mapStore.map.setOptions({ draggableCursor: null })
            
            // Make ROIs clickable again when stopping drawing
            this.setROIsClickable(true)
        },
        async generateRoute() {
            this.routeGenerated = true
            this.isLineChanged = false

            const data = { 
                field_id_1: this.startField_id,
                field_1_target: this.lines[0]
                    ? {
                        type: 'FeatureCollection',
                        features: [
                            {
                                id: '0',
                                type: 'Feature',
                                properties: {},
                                geometry: {
                                    type: 'LineString',
                                    coordinates: this.lines[0].coordinates.map(c => [c.lng(), c.lat()])
                                }
                            }
                        ]
                    }
                    : null,
                road: this.lines[1]
                    ? {
                        type: 'FeatureCollection',
                        features: [
                            {
                                id: '0',
                                type: 'Feature',
                                properties: {},
                                geometry: {
                                    type: 'LineString',
                                    coordinates: this.lines[1].coordinates.map(c => [c.lng(), c.lat()])
                                }
                            }
                        ]
                    }
                    : null,
                field_id_2: this.endField_id,
                field_2_target: this.lines[2]
                    ? {
                        type: 'FeatureCollection',
                        features: [
                            {
                                id: '0',
                                type: 'Feature',
                                properties: {},
                                geometry: {
                                    type: 'LineString',
                                    coordinates: this.lines[2].coordinates.map(c => [c.lng(), c.lat()])
                                }
                            }
                        ]
                    }
                    : null,
                    force_middle: this.forceMiddle
            }

            try {
                this.loading = true
                // reset the error message and downloadable data before generating the route
                this.clearErrorMessage()

                const res = await postGenerateFieldConnection(data)

                if (res.status >= 400) {
                    this.errorConnGen = true
                    this.errorMessage.reason = res.data.reason
                    this.errorMessage.info = res.data.info
                    this.geoRequestData = data
                    return res.data
                }
                this.drawGeneratedRoute(res)
                return true
            } catch (error) {
                console.error('Error generating route:', error)
                return false
            } finally {
                this.loading = false
            }
        },
        drawGeneratedRoute(geojsonRoute) {
            const mapStore = useMaps()
            if (!geojsonRoute || !geojsonRoute.features || !geojsonRoute.features.length) return

            // Clear any previously generated route
            if (this.generatedRoute) {
                this.generatedRoute.forEach(poly => {
                    toRaw(poly).setMap(null)
                })
                this.generatedRoute = []
            }

            this.generatedRoute = []

            geojsonRoute.features.forEach(feature => {
                if (feature.geometry && feature.geometry.type === 'LineString') {
                    const coordinates = feature.geometry.coordinates
                    const path = coordinates.map(([lng, lat]) => new mapStore.google.maps.LatLng(lat, lng))
                    const polyline = this.createPolyline(path, false)
                    toRaw(this.generatedRoute).push(polyline)
                }
            })
            for (let i = 0; i < this.generatedRoute.length; i++) {
                if (this.generatedRoute[i]) {
                    this.addArrowsToPolyline(this.generatedRoute[i], geojsonRoute.features[i])
                }
            }
        },
        createPolyline(path, clickable) {
            const mapStore = useMaps()
            const polyline = new mapStore.google.maps.Polyline({
                path,
                strokeColor: '#00FF00',
                strokeOpacity: 1.0,
                strokeWeight: 4,
                map: mapStore.map,
                zIndex: 1002,
                clickable: clickable
            })
            return polyline
        },
        async saveRoute() {
            // Check if we have generated routes
            if (!this.generatedRoute || this.generatedRoute.length === 0) {
                console.error('No generated routes to save')
                return false
            }
            /**
             * If only one field is selected, save a single route; 
             * if two fields are selected (double mode), save two routes—one for each field.
             */
            this.loading = true
            try {
                if (this.generatedRoute[0]) {
                    const data1 = {
                        field_id: this.startField_id,
                        parent_id: null,
                        driveable: true,
                        active: true,
                        type: 'entrypoint',
                        is_small: false,
                        geometry: {
                            type: 'LineString',
                            coordinates: this.generatedRoute[0].getPath().getArray().map(latLng => [latLng.lng(), latLng.lat()])
                        },
                        generator_version: 1,
                        commit_msg: 'add new field connection path'
                    }

                    await postSaveFieldConnection(data1)
                }

                if (this.endField_id && this.generatedRoute[1]) {
                    const data2 = {
                        field_id: this.endField_id,
                        parent_id: null,
                        driveable: true,
                        active: true,
                        type: 'entrypoint',
                        is_small: false,
                        geometry: {
                            type: 'LineString',
                            coordinates: this.generatedRoute[1].getPath().getArray().slice().reverse().map(latLng => [latLng.lng(), latLng.lat()])
                        },
                        generator_version: 1,
                        commit_msg: 'add new field connection path'
                    }

                    await postSaveFieldConnection(data2)
                }

                this.clearLines()

                return true
            } catch (error) {
                console.error('Error saving route:', error)
                return false
            } finally {
                this.loading = false
            }
        },
        async getFieldROIs(field_id, getSegments = false) {
            const roiStore = useRoiStore()
            try {
                const res = await getFieldROIs(field_id, true)

                if (!res || res?.length === 0) {
                    roiStore.setExistingElementsFromGeojson(null)
                    return
                }
                
                // Convert the API response to GeoJSON format expected by drawROIs
                if (res && Array.isArray(res) && res.length > 0) {
                    this.geojsonResponse = this.normalizeROIs(res)
                }
            } catch (error) {
                console.error('Error getting field connection:', error)
            }
            if (getSegments) {
                await this.getCutoutSegmentsToFieldAndAppend(field_id)
            }
            roiStore.setExistingElementsFromGeojson(this.geojsonResponse.features)
            this.drawROIs(field_id)
        },
        async getCutoutSegmentsToFieldAndAppend(field_id) {
            try{
                const head_segments = await getFieldCutoutHeadlandSegments(field_id)
                // Attach headland segments to matching ROIs by parent_roi_id
                if (head_segments && Array.isArray(head_segments) && head_segments.length > 0) {
                    this.geojsonResponse.features.forEach(feature => {
                        const matchingSegment = head_segments.find(segment => 
                        segment.parent_roi_id === feature.id || segment.parent_cutout_id === feature.id
                    )
                    if (matchingSegment) {
                        feature.properties.headlandsSegments = matchingSegment.geometry
                        feature.properties.head_segments_id = matchingSegment.id
                    }
                    })
            }
            } catch(error) {
                console.warn("Error while trying to get the headland segments. ", error)    
            }
        },
        normalizeROIs(rois) {
            const geojson = {
                type: 'FeatureCollection',
                features: rois.map(item => ({
                    id: item.id || item.geometries_id,
                    type: 'Feature',
                    properties: {
                        field_id: item.field_id,
                        type: item.type,
                        driveable: item.driveable,
                        active: item.active,
                        commit_msg: item.commit_msg,
                        is_small: item.is_small,
                        height: item.height, // not used yet
                        valid_from: item.valid_from, // not used yet
                        valid_to: item.valid_to, // not used yet
                        changed_by: item.changed_by, // not used yet
                        headlandsSegments: item?.headlandsSegments || null,
                        head_segments_id: item?.head_segments_id || null
                    },
                    geometry: item.geometry
                }))
            }
            return geojson
        },
        async getFarmROIs(farm_id) {
            try {
                const res = await getFarmROIs(farm_id)

                if (res && Array.isArray(res) && res.length > 0) {
                    this.geojsonResponse = this.normalizeROIs(res)
                    const roiStore = useRoiStore()
                    roiStore.setExistingElementsFromGeojson(this.geojsonResponse.features)
                    this.drawROIs(farm_id)
                }
                const fls = useFieldLayersStore()
                const roisLayers = fls.layers.filter(layer => layer.id.startsWith('roi_') || layer.id === 'cutout')
                roisLayers.forEach(layer => {
                    layer.visible = true
                })
            } catch (error) {
                console.error('Error getting farm ROIs:', error)
            }
        },
        clearFarmROIs(farm_id) {
            const fls = useFieldLayersStore()
            // No farm id or no farm ROIs: just rebuild the flat list from what is left
            if (!farm_id || !this.roisByFieldId || !this.roisByFieldId[farm_id]) {
                // Put all ROIs still in roisByFieldId into generatedROIs
                this.generatedROIs = this.roisByFieldId
                    ? Object.values(this.roisByFieldId).flat()
                    : []
                return
            }
            // Hide each farm ROI on the map
            this.roisByFieldId[farm_id].forEach(poly => {
                const rawPoly = toRaw(poly)
                // Shapes that use setMap (polyline, polygon, etc.)
                if (rawPoly.setMap) {
                    rawPoly.setMap(null)
                } else if (rawPoly.map !== undefined) {
                    // Advanced marker uses .map instead of setMap
                    rawPoly.map = null
                }
            })
            // Drop the farm key from roisByFieldId
            delete this.roisByFieldId[farm_id]
            // Flat list = field ROIs + any other keys (not this farm)
            this.generatedROIs = Object.values(this.roisByFieldId).flat()
        },
        clearFieldROIs(field_id = null) {
            this.closeAllOpenInfoWindows()

            // If a field_id is specified, clear only the ROIs for that field
            if (field_id && this.roisByFieldId && this.roisByFieldId[field_id]) {
                this.roisByFieldId[field_id].forEach(poly => {
                    const rawPoly = toRaw(poly)
                    if (rawPoly.setMap) {
                        rawPoly.setMap(null)
                    } else if (rawPoly.map !== undefined) {
                        rawPoly.map = null
                    }
                })
                delete this.roisByFieldId[field_id]
            } else {
                // Clear all ROIs
                this.removeRoiMapClickListener()
                if (this.roisByFieldId) {
                    Object.values(this.roisByFieldId).flat().forEach(poly => {
                        const rawPoly = toRaw(poly)
                        if (rawPoly.setMap) {
                            rawPoly.setMap(null)
                        } else if (rawPoly.map !== undefined) {
                            rawPoly.map = null
                        }
                    })
                    this.roisByFieldId = {}
                }
                
                if (this.generatedROIs) {
                    this.generatedROIs.forEach(poly => {
                        const rawPoly = toRaw(poly)
                        if (rawPoly.setMap) {
                            rawPoly.setMap(null)
                        } else if (rawPoly.map !== undefined) {
                            rawPoly.map = null
                        }
                    })
                    this.generatedROIs = []
                }
                this.geojsonResponse = null
            }
            
            // Update generatedROIs
            this.generatedROIs = this.roisByFieldId ? Object.values(this.roisByFieldId).flat() : []
        },
        // Toggle ROIs visibility for a field (click to show/hide)
        async toggleFieldROIs(fieldId) {
            // If ROIs are shown, hide them and close their InfoWindows
            if (this.viewROIsData[fieldId]) {
                this.viewROIsData[fieldId].forEach(roi => {
                    this.closeInfoWindowsForROI(roi)
                    toRaw(roi).setMap(null)
                })
                delete this.viewROIsData[fieldId]
                return
            }

            try {
                const response = await getFieldROIs(fieldId, true)
                if (!response?.length) {
                    this.warning.type = 'info'
                    this.warning.message = 'No zones found for this field'
                    return
                }
                
                const rois = []
                const geojson = this.normalizeROIs(response)

                geojson.features.forEach(feature => {
                    if (!feature.properties.field_id) {
                        feature.properties.field_id = fieldId
                    }

                    const geom = feature.geometry
                    if (!geom?.type) return

                    if (geom.type === 'LineString') {
                        const roi = this.drawLineString(feature)
                        if (roi) {
                            roi.roiType = feature.properties?.type || 'entrypoint'
                            rois.push(roi)
                        }
                    } else if (geom.type === 'Polygon') {
                        const roi = this.drawPolygon(feature)
                        if (roi) {
                            roi.roiType = feature.properties?.type || 'obstacle'
                            rois.push(roi)
                        }
                    } else if (geom.type === 'MultiPolygon') {
                        const mpRois = this.drawMultiPolygon(feature)
                        if (mpRois?.length) {
                            const t = feature.properties?.type || 'obstacle'
                            mpRois.forEach((r) => {
                                r.roiType = t
                                rois.push(r)
                            })
                        }
                    } else if (geom.type === 'Point') {
                        const roi = this.drawPoint(feature)
                        if (roi) {
                            roi.roiType = feature.properties?.type || 'unknown'
                            rois.push(roi)
                        }
                    }
                })
                
                this.viewROIsData[fieldId] = rois
                this.warning.type = 'success'
                this.warning.message = 'Zones loaded successfully'
            } catch (e) {
                console.error('Error loading field ROIs:', e)
            }
        },
        // Clear all viewed ROIs
        clearViewROIs() {
            const roi = useRoiStore()
            roi.clearAllElements()
            Object.values(this.viewROIsData).flat().forEach(roi => toRaw(roi).setMap(null))
            this.viewROIsData = {}
        },
        drawROIs(field_id) {
            if (!this.geojsonResponse || !this.geojsonResponse.features || !this.geojsonResponse.features.length) return
        
            if (!this.roisByFieldId) {
                this.roisByFieldId = {}
            }

            if (this.roisByFieldId[field_id]) {
                this.roisByFieldId[field_id].forEach(roi => {
                    const rawROI = toRaw(roi)
                    if (rawROI.setMap) {
                        rawROI.setMap(null)
                    } else if (rawROI.map !== undefined) {
                        rawROI.map = null
                    }
                })
                this.roisByFieldId[field_id] = []
            }

            const newROIs = []
        
            this.geojsonResponse.features.forEach(feature => {
                let roi = null
                // Handle LineString
                if (feature.geometry && feature.geometry.type === 'LineString') {
                    roi = this.drawLineString(feature)
                    if (roi) roi.roiType = 'entrypoint'
                }
                // Handle Point
                if (feature.geometry && feature.geometry.type === 'Point') {
                    roi = this.drawPoint(feature)
                    if (roi) roi.roiType = feature.properties?.type || 'unknown'
                }
                // Handle MultiPolygon
                if (feature.geometry && feature.geometry.type === 'MultiPolygon') {
                    const mpRois = this.drawMultiPolygon(feature)
                    if (mpRois?.length) {
                        const t = feature.properties?.type || 'obstacle'
                        mpRois.forEach((r) => {
                            r.roiType = t
                            newROIs.push(r)
                        })
                    }
                }
                // Handle Polygon
                if (feature.geometry && feature.geometry.type === 'Polygon') {
                    roi = this.drawPolygon(feature)
                    if (roi) roi.roiType = feature.properties?.type || 'obstacle'
                }
                if (roi) {
                    newROIs.push(roi)
                }
            })
            
            this.roisByFieldId[field_id] = newROIs
            this.generatedROIs = Object.values(this.roisByFieldId).flat()
            this.ensureRoiMapClickListener()
        },
        ensureRoiMapClickListener() {
            const mapStore = useMaps()
            const roiStore = useRoiStore()

            if (this.roiMapClickListener || !mapStore.map || !mapStore.google) return

            this.roiMapClickListener = mapStore.google.maps.event.addListener(
                toRaw(mapStore.map),
                'click',
                () => {
                    this.closeAllOpenInfoWindows()
                    roiStore.highlightedRoiId = null
                }
            )
        },
        drawLineString(feature) {
            const mapStore = useMaps()
            const roiStore = useRoiStore()

            const coordinates = feature.geometry.coordinates
            const path = coordinates.map(([lng, lat]) => new mapStore.google.maps.LatLng(lat, lng))
            const polyline = this.createPolyline(path, true)

            // Add click listener for InfoWindow
            polyline.addListener('click', (event) => {
                this.closeAllOpenInfoWindows()
                roiStore.highlightRoiInList(feature.id)

                const { content, downloadButtonId, deleteButtonId } = this.createROIInfoWindowContent(feature, 'LineString')
                const infoWindow = new mapStore.google.maps.InfoWindow({
                    content: content,
                    position: event.latLng
                })
                infoWindow.open({
                    map: toRaw(mapStore.map)
                })
                this.wireRoiInfoWindowDownload(infoWindow, feature, downloadButtonId)
                this.wireRoiInfoWindowDelete(infoWindow, feature, deleteButtonId)

                this.openInfoWindows.push({
                    roi: toRaw(polyline),
                    infoWindow: infoWindow
                })
            })

            // Store feature id for later reference
            polyline.roiId = feature.id

            this.addArrowsToPolyline(polyline, feature)
            polyline.setOptions({ clickable: true })

            return polyline
        },
        drawPoint(feature) {
            const mapStore = useMaps()
            const roiStore = useRoiStore()

            const [lng, lat] = feature.geometry.coordinates
            const position = new (toRaw(mapStore.google)).maps.LatLng(lat, lng)
            
            // Determine marker color and text based on feature properties
            const type = feature.properties?.type || feature.type || 'obstacle'
            const isSmall = feature.properties?.is_small || feature.is_small || false
            
            // Choose color based on type
            let backgroundColor = '#FF0000' // default red for obstacles
            let glyphText = 'O' // default 'O' for obstacle
            
            if (type === 'entrypoint') {
                backgroundColor = '#00FF00' // green for entrypoint
                glyphText = 'E'
            } else if (type === 'obstacle') {
                backgroundColor = isSmall ? '#FFA500' : '#FF0000' // orange for small, red for large
                glyphText = isSmall ? 'S' : 'L' // 's' for small, 'L' for large
            } else if (type === 'logistic') {
                backgroundColor = '#0000FF' // blue for logistic
                glyphText = 'L'
            }
            
            // Create marker with PinElement
            const pinElement = new mapStore.google.maps.marker.PinElement({
                background: backgroundColor,
                borderColor: '#000000',
                glyphColor: '#FFFFFF',
                scale: isSmall ? 1.0 : 1.2,
                glyphText: glyphText,
            })
        
            // Minimalist title for marker
            const title = `${type} #${feature.id}`
            
            const marker = new mapStore.google.maps.marker.AdvancedMarkerElement({
                position: position,
                map: toRaw(mapStore.map),
                title: title,
                content: pinElement,
                zIndex: 1003,
                gmpClickable: true
            })
        
            marker.addEventListener('gmp-click', () => {
                this.closeAllOpenInfoWindows()
                roiStore.highlightRoiInList(feature.id)

                const { content, downloadButtonId, deleteButtonId } = this.createROIInfoWindowContent(feature, 'Point')
                const infoWindow = new mapStore.google.maps.InfoWindow({
                    content: content
                })

                infoWindow.open({
                    map: toRaw(mapStore.map),
                    anchor: marker
                })
                this.wireRoiInfoWindowDownload(infoWindow, feature, downloadButtonId)
                this.wireRoiInfoWindowDelete(infoWindow, feature, deleteButtonId)

                this.openInfoWindows.push({
                    roi: toRaw(marker),
                    infoWindow: infoWindow
                })
            })
            
            // Store feature id for later reference
            marker.roiId = feature.id

            return marker
        },
        drawMultiPolygon(feature) {
            const mapStore = useMaps()
            const roiStore = useRoiStore()

            const multiPolygonCoords = feature.geometry.coordinates
            const polygons = []

            multiPolygonCoords.forEach((polygonRings) => {
                if (!polygonRings || polygonRings.length === 0) return

                const paths = polygonRings.map((ring) => geoJsonRingToOpenPath(ring))
                const strokeColor = feature.properties.active ? (feature.properties?.driveable ? '#32CD32' : '#CC0000') : '#555555'
                const fillColor = feature.properties.active ? (feature.properties?.is_small ? '#FFA500' : '#FF6666') : '#696969'

                const polygon = new mapStore.google.maps.Polygon({
                    paths: paths,
                    strokeColor: strokeColor,
                    strokeOpacity: 1.0,
                    strokeWeight: 4,
                    fillColor: fillColor,
                    fillOpacity: 0.5,
                    map: mapStore.map,
                    zIndex: 1002,
                    gmpClickable: true
                })

                polygon.addListener('click', (event) => {
                    this.closeAllOpenInfoWindows()
                    roiStore.highlightRoiInList(feature.id)

                    const { content, downloadButtonId, deleteButtonId } = this.createROIInfoWindowContent(feature, 'MultiPolygon')
                    const infoWindow = new mapStore.google.maps.InfoWindow({
                        content: content,
                        position: event.latLng
                    })
                    infoWindow.open({
                        map: toRaw(mapStore.map)
                    })
                    this.wireRoiInfoWindowDownload(infoWindow, feature, downloadButtonId)
                    this.wireRoiInfoWindowDelete(infoWindow, feature, deleteButtonId)

                    this.openInfoWindows.push({
                        roi: toRaw(polygon),
                        infoWindow: infoWindow
                    })
                })

                polygon.roiId = feature.id
                polygons.push(polygon)
            })

            return polygons
        },
        drawPolygon(feature) {
            const mapStore = useMaps()
            const roiStore = useRoiStore()

            const polygonRings = feature.geometry.coordinates
            if (!polygonRings || polygonRings.length === 0) return
                
            const paths = polygonRings.map(ring => geoJsonRingToOpenPath(ring))
            const strokeColor = feature.properties.active ? (feature.properties?.driveable ? '#32CD32' : '#CC0000') : '#555555'
            const fillColor = feature.properties.active ? (feature.properties?.is_small ? '#FFA500' : '#FF6666') : '#696969'
            const polygon = new mapStore.google.maps.Polygon({
                paths: paths,
                strokeColor: strokeColor,
                strokeOpacity: 1.0,
                strokeWeight: 4,
                fillColor: fillColor,
                fillOpacity: 0.5,
                map: mapStore.map,
                zIndex: 1002,
                gmpClickable: true
            })

            polygon.addListener('click', (event) => {
                this.closeAllOpenInfoWindows()
                roiStore.highlightRoiInList(feature.id)

                const { content, downloadButtonId, deleteButtonId } = this.createROIInfoWindowContent(feature, 'Polygon')
                const infoWindow = new mapStore.google.maps.InfoWindow({
                    content: content,
                    position: event.latLng
                })
                infoWindow.open({
                    map: toRaw(mapStore.map)
                })
                this.wireRoiInfoWindowDownload(infoWindow, feature, downloadButtonId)
                this.wireRoiInfoWindowDelete(infoWindow, feature, deleteButtonId)
        
                this.openInfoWindows.push({
                    roi: toRaw(polygon),
                    infoWindow: infoWindow
                })
            })
            
            // Store feature id for later reference
            polygon.roiId = feature.id

            return polygon
        },
        closeInfoWindowsForROI(roi) {
            const rawROI = toRaw(roi)

            // Close all InfoWindows associated with this ROI
            this.openInfoWindows = this.openInfoWindows.filter(item => {
                if (toRaw(item.roi) === rawROI) {
                    if (item.infoWindow) {
                        item.infoWindow.close()
                        item.infoWindow = null
                    }
                    return false
                }
                return true
            })
        },
        removeRoiMapClickListener() {
            const mapStore = useMaps()

            if (this.roiMapClickListener && mapStore.google?.maps?.event) {
                mapStore.google.maps.event.removeListener(this.roiMapClickListener)
            }
            this.roiMapClickListener = null
        },
        closeAllOpenInfoWindows() {
            this.openInfoWindows.forEach(item => {
                if (item.infoWindow) {
                    item.infoWindow.close()
                    item.infoWindow = null
                }
            })
            this.openInfoWindows = []
        },
        removeViewedRoiById(fieldId, roiID) {
            const fieldRois = this.viewROIsData[fieldId]
            if (!fieldRois) return

            for (let i = 0; i < fieldRois.length; i++) {
                if (toRaw(fieldRois[i]).roiId === roiID) {
                    toRaw(fieldRois[i]).setMap?.(null)
                    fieldRois.splice(i, 1)
                    break
                }
            }

            if (!fieldRois.length) {
                delete this.viewROIsData[fieldId]
            }
        },
        wireRoiInfoWindowDownload(infoWindow, feature, downloadButtonId) {
            const mapStore = useMaps()
            mapStore.google.maps.event.addListenerOnce(infoWindow, 'domready', () => {
                const btn = document.getElementById(downloadButtonId)
                if (!btn) return
                btn.addEventListener('click', async (e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    await this.downloadROI({
                        id: feature.id,
                        geometry: feature.geometry,
                        properties: feature.properties ?? {},
                    })
                })
            })
        },
        wireRoiInfoWindowDelete(infoWindow, feature, deleteButtonId) {
            const mapStore = useMaps()
            const modal = useModal()
            const roi = useRoiStore()

            mapStore.google.maps.event.addListenerOnce(infoWindow, 'domready', () => {
                const btn = document.getElementById(deleteButtonId)
                if (!btn) return
                btn.addEventListener('click', async (e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    const confirmed = await modal.fire({
                        title: i18n.global.t('Delete zone'),
                        text: i18n.global.t('Are you sure you want to delete this zone?'),
                        type: 'warning',
                        showCancelButton: true,
                        confirmButtonText: i18n.global.t('Yes, delete it'),
                        cancelButtonText: i18n.global.t('No, keep it'),
                    })
                    if (!confirmed) return

                    try {
                        const roiID = feature.id
                        const fieldID = feature.properties.field_id

                        if (!fieldID || !roiID) {
                            console.error('Field ID or ROI ID not found')
                            return
                        }

                        const ok = await deleteOldFieldROI(fieldID, roiID)
                        if (ok) {
                            const overlay = this.generatedROIs?.find(roi => toRaw(roi).roiId === roiID)
                            if (overlay) {
                                toRaw(overlay).setMap?.(null)
                            }

                            this.removeViewedRoiById(fieldID, roiID)
                            
                            // Remove from UI list (in case if is Zone Planning)
                            if (roi.selectedElementId === roiID) {
                                roi.clearSelection()
                            }

                            roi.geometryElements = roi.geometryElements.filter(el => el.id !== roiID)
                            infoWindow.close()
                            this.openInfoWindows = this.openInfoWindows.filter(
                                (item) => item.infoWindow !== infoWindow
                            )
                        }
                    } catch (error) {
                        console.error('Error deleting ROI:', error)
                    }
                })
            })
        },
        createROIInfoWindowContent(feature, geometryType) {
            const downloadButtonId = `roi-infowin-dl-${String(feature.id)}-${uuid()}`
            const deleteButtonId = `roi-infowin-del-${String(feature.id)}-${uuid()}`
            const type = feature.properties?.type || feature.type || 'unknown'
            const commitMsg = feature.properties?.commit_msg || feature.commit_msg || ''
            const height = feature.properties?.height ?? feature.height ?? null
            const validFrom = feature.properties?.valid_from || feature.valid_from || ''
            const validTo = feature.properties?.valid_to || feature.valid_to || ''
            const changedBy = feature.properties?.changed_by || feature.changed_by || ''
            const fieldId = feature.properties?.field_id || feature.field_id || ''
            const driveable = feature.properties?.driveable !== undefined ? feature.properties.driveable : null
            const active = feature.properties?.active !== undefined ? feature.properties.active : null
            const isSmall = feature.properties?.is_small || feature.is_small || false

             // Map type to translation key
            const typeTranslations = {
                'obstacle': i18n.global.t('Obstacle'),
                'entrypoint': i18n.global.t('Entry Point'),
                'logistic': i18n.global.t('Logistic')
            }
            const translatedType = typeTranslations[type] || type.charAt(0).toUpperCase() + type.slice(1)
            
            let content = `
                <div style="padding: 12px; min-width: 200px; font-family: Arial, sans-serif;">
                    <div style="margin-bottom: 12px; border-bottom: 1px solid #e0e0e0; padding-bottom: 8px;">
                        <span style="font-weight: 600; color: #2c3e50; font-size: 16px;">
                            ${translatedType} - id: ${feature.id}
                        </span>
                    </div>
                    <div style="display: flex; flex-direction: column; gap: 6px;">
            `
            
            if (fieldId) {
                content += `
                    <div>
                        <span style="font-weight: 600; color: #2c3e50;">${i18n.global.t('Field ID')}:</span>
                        <span style="margin-left: 8px; color: #34495e;">${fieldId}</span>
                    </div>
                `
            }
            
            if (commitMsg) {
                content += `
                    <div>
                        <span style="font-weight: 600; color: #2c3e50;">${i18n.global.t('Message')}:</span>
                        <span style="margin-left: 8px; color: #34495e;">${commitMsg}</span>
                    </div>
                `
            }
            
            const hasHeight = height !== null && height !== undefined && height !== ''
            content += `
                <div>
                    <span style="font-weight: 600; color: #2c3e50;">${i18n.global.t('Height')}:</span>
                    <span style="margin-left: 8px; color: #34495e;">${hasHeight ? height : 'N/A'} m</span>
                </div>
            `
            
            if (validFrom) {
                const dateFrom = formatDate(validFrom)
                content += `
                    <div>
                        <span style="font-weight: 600; color: #2c3e50;">${i18n.global.t('Valid From')}:</span>
                        <span style="margin-left: 8px; color: #34495e;">${dateFrom.date} ${dateFrom.time}</span>
                    </div>
                `
            }
            
            if (validTo) {
                const dateTo = formatDate(validTo)
                content += `
                    <div>
                        <span style="font-weight: 600; color: #2c3e50;">${i18n.global.t('Valid To')}:</span>
                        <span style="margin-left: 8px; color: #34495e;">${dateTo.date} ${dateTo.time}</span>
                    </div>
                `
            }
            
            if (changedBy) {
                content += `
                    <div>
                        <span style="font-weight: 600; color: #2c3e50;">${i18n.global.t('Changed by')}:</span>
                        <span style="margin-left: 8px; color: #34495e;">${changedBy}</span>
                    </div>
                `
            }
            
            if (driveable !== null) {
                content += `
                    <div>
                        <span style="font-weight: 600; color: #2c3e50;">${i18n.global.t('Driveable')}:</span>
                        <span style="margin-left: 8px; color: ${driveable ? '#27ae60' : '#e74c3c'}">${driveable ? i18n.global.t('Yes') : i18n.global.t('No')}</span>
                    </div>
                `
            }

            // if (isSmall !== null) {
            //     content += `
            //         <div>
            //             <span style="font-weight: 600; color: #2c3e50;">${i18n.global.t('Size')}:</span>
            //             <span style="margin-left: 8px; color: ${isSmall ? '#27ae60' : '#e74c3c'}">${isSmall ? i18n.global.t('Small') : i18n.global.t('Large')}</span>
            //         </div>
            //     `
            // }
            
            if (active !== null) {
                content += `
                    <div>
                        <span style="font-weight: 600; color: #2c3e50;">${i18n.global.t('Active')}:</span>
                        <span style="margin-left: 8px; color: ${active ? '#27ae60' : '#e74c3c'}">${active ? i18n.global.t('Yes') : i18n.global.t('No')}</span>
                    </div>
                `
            }

            const geometryTranslations = {
                'Polygon': i18n.global.t('Polygon'),
                'MultiPolygon': i18n.global.t('Multi Polygon'),
                'LineString': i18n.global.t('Line'),
                'Point': i18n.global.t('Point')
            }
            const translatedGeometry = geometryTranslations[geometryType] || geometryType.charAt(0).toUpperCase() + geometryType.slice(1)
            
            // Add geometry type if needed
            if (geometryType) {
                content += `
                    <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e0e0e0;">
                        <span style="font-weight: 600; color: #2c3e50;">${i18n.global.t('Geometry')}:</span>
                        <span style="margin-left: 8px; color: #34495e;">${translatedGeometry}</span>
                    </div>
                `
            }

            // Download + delete (same row)
            content += `
                <div style="display: flex; flex-direction: row; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: 8px;">
                    <button type="button" id="${downloadButtonId}" style="padding: 8px; background-color: #27ae60; color: white; border: none; border-radius: 4px; cursor: pointer;">
                        <i class="fa-solid fa-download"></i> ${i18n.global.t('Download')}
                    </button>
                    <button type="button" id="${deleteButtonId}" style="padding: 8px; background-color: #e74c3c; color: white; border: none; border-radius: 4px; cursor: pointer;">
                        <i class="fa-solid fa-trash"></i> ${i18n.global.t('Delete')}
                    </button>
                </div>
            `
            
            content += `
                    </div>
                </div>
            `
            
            return { content, downloadButtonId, deleteButtonId }
        },
        async downloadROI(feature) {
            const fileName = `roi_${feature.properties.type}_${moment().format('YYYY-MM-DD-HH-mm')}`
            const roiFeature = {
                type: 'Feature',
                geometry: feature.geometry,
                properties: feature.properties
            }
            await downloadFeatureAsShapefile(roiFeature, fileName)
        },
        addArrowsToPolyline(polyline, feature) {
            const ps = usePlanner()
            const originalPath = polyline.getPath().getArray()
            
            if (feature.id === '1') {
                // Inverse the path for arrows
                polyline.setPath([...originalPath].reverse())
                ps.addDirectionalArrows(
                    [polyline],
                    '#00FF00',
                    '#000000'
                )
                this.animateCircle(polyline)
            } else {
                polyline.setPath(originalPath) // return to the correct path for display
                ps.addDirectionalArrows(
                    [polyline],
                    '#00FF00',
                    '#000000'
                )
                this.animateCircle(polyline)
            }
        },
        addFieldListener(field) {
            if (!field?.poly) return
            const selectField = () => {
                if (this.loading) return

                if (!this.startField_id) {
                    this.startField_id = field.id
                    this.onStartFieldChange(field.id)
                } else if (this.connectionMode === 'double' && !this.endField_id && this.startField_id != field.id) {
                    this.endField_id = field.id
                    this.onEndFieldChange(field.id)
                }
            }
            this.fieldListeners.push(field.poly.addListener('click', selectField))
            this.addFieldLabelListener(field, selectField)
        },
        /**
         * Same click behaviour on the field name label as on the field polygon.
         * Uses `spider_click` so overlapping labels still spread out first instead of picking one.
         */
        addFieldLabelListener(field, handler) {
            if (!field?.popup) return

            const popup = toRaw(field.popup)
            popup.gmpClickable = true
            this.fieldListeners.push(popup.addListener('spider_click', handler))
        },
        addFieldListeners(fieldId = null) {
            const mapStore = useMaps()

            mapStore.fields.forEach(field => {
                this.addFieldListener(field)
                if (fieldId && field.id !== fieldId) {
                    return
                }
                this.setFieldStyle(field, false)
            })
        },
        // Clear all field listeners
        clearFieldListeners() {
            const mapStore = useMaps()
            this.fieldListeners.forEach(listener => {
                mapStore.google.maps.event.removeListener(listener)
            })
            this.fieldListeners = []
        },
        // Add ROI toggle listeners for Obstacles mode
        addROIToggleListeners(selectedFieldId) {
            const mapStore = useMaps()
            
            mapStore.fields.forEach(field => {
                if (field.poly && field.id !== selectedFieldId) {
                    const poly = toRaw(field.poly)
                    poly.setOptions({ clickable: true })

                    const toggleROIs = () => this.toggleFieldROIs(field.id)
                    this.fieldListeners.push(poly.addListener('click', toggleROIs))
                    this.addFieldLabelListener(field, toggleROIs)
                }
            })
        },
        // Show other fields in gray with optional ROI toggle listeners
        showOtherFields() {
            const mapStore = useMaps()
            const selectedFieldId = this.startField_id
            
            // Clear existing listeners first
            this.clearFieldListeners()
            
            // Exclude selected fields
            const excludeIds = [this.startField_id, this.endField_id].filter(Boolean)
            
            mapStore.fields.forEach(f => {
                // Hide popup of selected fields, show others
                if (excludeIds.includes(f.id)) {
                    if (f.popup) {
                        toRaw(f.popup).setMap(null)
                    }
                    return
                }
                if (f.poly) {
                    toRaw(f.poly).setMap(toRaw(mapStore.map))
                    toRaw(f.poly).setVisible(true)
                    this.setFieldStyle(f, false)
                }
                if (f.popup) {
                    toRaw(f.popup).setMap(toRaw(mapStore.map))
                }
            })
            
            // Add ROI toggle listeners to view ROIs from other fields
            this.addROIToggleListeners(selectedFieldId)
            
            this.otherFieldsVisible = true
        },
        // Hide other fields (gray fields)
        hideOtherFields() {
            const mapStore = useMaps()
            const excludeIds = [this.startField_id, this.endField_id].filter(Boolean)
            
            this.clearFieldListeners()
            this.clearViewROIs()
            
            mapStore.fields.forEach(f => {
                if (excludeIds.includes(f.id)) {
                    return
                }
                if (f.poly) {
                    toRaw(f.poly).setMap(null)
                }
                if (f.popup) {
                    toRaw(f.popup).setMap(null)
                }
            })
            
            this.otherFieldsVisible = false
        },
        // Toggle other fields visibility
        toggleOtherFields() {
            if (this.otherFieldsVisible) {
                this.hideOtherFields()
            } else {
                this.showOtherFields()
            }
        },
        async uploadLineFile(file) {
            const formData = new FormData()
            formData.append('file', file)
            const response = await postExtractGeometryFromShape(formData)
            if (!response) {
                return 'error'
            }
            if (response.line && response.line.type === 'LineString') {
                const mapStore = useMaps()
                
                // Convert coordinates from [lng, lat] to Google Maps LatLng objects
                const coordinates = response.line.coordinates.map(([lng, lat]) => 
                    new mapStore.google.maps.LatLng(lat, lng)
                )

                if (coordinates.length < 2) {
                    return 'point'
                }

                if (coordinates.length > 2) {
                    return 'to_many_points'
                }

                // Create a new polyline for the uploaded line
                const polyline = new mapStore.google.maps.Polyline({
                    path: coordinates,
                    strokeColor: '#FF0000',
                    strokeOpacity: 1.0,
                    strokeWeight: 3,
                    map: toRaw(mapStore.map),
                    clickable: false,
                    zIndex: 1001
                })
                
                // Store the line in the same format as manual drawing
                this.lines.push({
                    coordinates: [...coordinates],
                    polyline: polyline
                })
                
                // Update currentLine to match the uploaded line
                this.currentLine = {
                    coordinates: [...coordinates],
                    polyline: polyline
                }

                this.configLineArrowsAndFields()
                
                this.completedLines++

                if (this.lines[1]) {
                    // When toggle is turned ON and road line exists, enforce the length
                    if (this.forceRoadLineLength) {
                        this.enforceRoadLineLength(this.lines[1])
                        this.isLineChanged = true
                    }
                }
                
                // If all lines are completed, finish drawing
                if (this.completedLines >= this.totalLines) {
                    this.finishDrawing()
                }
            } else {
                return 'no_line'
            }
            return 'success'
        },
        enforceRoadLineLength(line) {
            const mapStore = useMaps()
            const path = line.polyline.getPath()
            const start = path.getAt(0)
            const end = path.getAt(1)
        
            // Calculate new end point at exactly ROAD_LENGTH_METERS from start in the direction of end
            const bearing = mapStore.google.maps.geometry.spherical.computeHeading(start, end)
            const newEnd = mapStore.google.maps.geometry.spherical.computeOffset(start, ROAD_LINE_LENGTH_METERS_WITH_OFFSET, bearing)
            
            // Update the polyline and coordinates
            line.polyline.setPath([start, newEnd])
            line.coordinates = [start, newEnd]
        },
        handleRoadLineDrag(line, initialStart, initialEnd){
            const mapStore = useMaps()
            let isDragging = false
            let dragStartLatLng = null
            let originalStart = null
            let originalEnd = null
                
            const stopDragging = () => {
                if (isDragging) {
                    isDragging = false
                    dragStartLatLng = null
                    originalStart = null
                    originalEnd = null
                    line.polyline.setOptions({ editable: true })
                    mapStore.map.setOptions({ draggable: true, draggableCursor: null })
                    // Re-enable field clickability after drag
                    this.handleFieldClickable(true)
                    // Make ROIs clickable again after drag
                    this.setROIsClickable(true)

                    // Update stored coordinates
                    const path = line.polyline.getPath()
                    const start = path.getAt(0)
                    const end = path.getAt(1)

                    if (this.hasCoordinatesChanged(initialStart, initialEnd, start, end)) {

                        if (this.forceRoadLineLength) {
                            line.coordinates = [start, end]
                            this.enforceRoadLineLength(line)
                            const newPath = line.polyline.getPath()
                            initialStart = newPath.getAt(0)
                            initialEnd = newPath.getAt(1)
                        } else {
                            initialStart = start
                            initialEnd = end
                            line.coordinates = [start, end]
                        }
                        this.isLineChanged = true
                    }
                }
            }

            const mousedownListener = line.polyline.addListener('mousedown', (e) => {
                if (e.vertex === undefined) {
                    // Clicked on line body, start manual drag
                    isDragging = true
                    dragStartLatLng = e.latLng
                    const path = line.polyline.getPath()
                    originalStart = path.getAt(0)
                    originalEnd = path.getAt(1)
                    line.polyline.setOptions({ editable: false })
                    mapStore.map.setOptions({ draggable: false, draggableCursor: 'move' })
                    this.handleFieldClickable(false)
                    // Make ROIs non-clickable when dragging
                    this.setROIsClickable(false)
                }
            })
            this.lineListeners.push(mousedownListener)

            const mousemoveListener = mapStore.map.addListener('mousemove', (e) => {
                if (isDragging && dragStartLatLng && originalStart && originalEnd) {
                    const latOffset = e.latLng.lat() - dragStartLatLng.lat()
                    const lngOffset = e.latLng.lng() - dragStartLatLng.lng()

                    const newStart = new mapStore.google.maps.LatLng(
                        originalStart.lat() + latOffset,
                        originalStart.lng() + lngOffset
                    )
                    const newEnd = new mapStore.google.maps.LatLng(
                        originalEnd.lat() + latOffset,
                        originalEnd.lng() + lngOffset
                    )

                    line.polyline.setPath([newStart, newEnd])
                }
            })
            this.lineListeners.push(mousemoveListener)

            // Make sure to cath the mouseup
            document.addEventListener('mouseup', stopDragging)
            this.documentListeners.push(stopDragging)

            // Still handle vertex dragging via mouseup on polyline
            const polylineMouseupListener = line.polyline.addListener('mouseup', () => {
                const path = line.polyline.getPath()
                const start = path.getAt(0)
                const end = path.getAt(1)

                if (this.hasCoordinatesChanged(initialStart, initialEnd, start, end)) {

                    if (this.forceRoadLineLength && !isDragging) {
                        line.coordinates = [start, end]
                        this.enforceRoadLineLength(line)
                        const newPath = line.polyline.getPath()
                        initialStart = newPath.getAt(0)
                        initialEnd = newPath.getAt(1)
                    } else {
                        initialStart = start
                        initialEnd = end
                        line.coordinates = [start, end]
                    }
                    this.isLineChanged = true
                }
            })
            this.lineListeners.push(polylineMouseupListener)
        },
        handleFieldLineSnap(line, initialStart, initialEnd) {
            const mapStore = useMaps()
            // Handle non-road lines (vertex movement only)
            const mousedownListener = line.polyline.addListener('mousedown', (e) => {
                if (e.vertex === undefined) {
                    line.polyline.setOptions({ editable: false })
                    setTimeout(() => {
                        line.polyline.setOptions({ editable: true })
                    }, 10)
                    return
                }
            })
            this.lineListeners.push(mousedownListener)

            const mouseupListener = line.polyline.addListener('mouseup', () => {
                line.polyline.setOptions({ draggable: false })
                const path = line.polyline.getPath()
                const start = path.getAt(0)
                const end = path.getAt(1)
                const hasChanged = this.hasCoordinatesChanged(initialStart, initialEnd, start, end)
                // Check if the line was actually moved
                if (hasChanged) {
                    const trackSystem = mapStore.draw.field.abLines.concat(mapStore.draw.field.headlanes)
                    let snapped_start = getClosestPointPolyline({lat: start.lat(), lng: start.lng()}, trackSystem)
                    let snapped_end = getClosestPointPolyline({lat: end.lat(), lng: end.lng()}, trackSystem)
                    snapped_start = new mapStore.google.maps.LatLng(snapped_start.lat, snapped_start.lng)
                    snapped_end = new mapStore.google.maps.LatLng(snapped_end.lat, snapped_end.lng)
                
                    if (snapped_start && snapped_end) {
                        line.polyline.setPath([snapped_start, snapped_end])
                        initialStart = snapped_start
                        initialEnd = snapped_end
                        line.coordinates = [snapped_start, snapped_end]
                    } else {
                        initialStart = start
                        initialEnd = end
                        line.coordinates = [start, end]
                    }
                    
                    this.isLineChanged = true
                }
            })
            this.lineListeners.push(mouseupListener)
        },
        hasCoordinatesChanged(initialStart, initialEnd, start, end){
            return (
                start.lat() !== initialStart.lat() ||
                start.lng() !== initialStart.lng() ||
                end.lat() !== initialEnd.lat() ||
                end.lng() !== initialEnd.lng()
            )
        },
        rotateLine(lineIndex, angleDegrees) {
            if (!this.lines[lineIndex] || this.lines[lineIndex].coordinates.length < 2) return
            
            const mapStore = useMaps()
            const line = this.lines[lineIndex]
            
            // Save center point and current length before rotation
            const start = line.coordinates[0]
            const end = line.coordinates[line.coordinates.length - 1]
            const centerLat = (start.lat() + end.lat()) / 2
            const centerLng = (start.lng() + end.lng()) / 2
            const center = new mapStore.google.maps.LatLng(centerLat, centerLng)
            const currentLength = mapStore.google.maps.geometry.spherical.computeDistanceBetween(start, end)
            const halfLength = currentLength / 2
            
            // Convert Google Maps coordinates to GeoJSON format [lng, lat]
            const coords = line.coordinates.map(coord => [coord.lng(), coord.lat()])
            const lineString = turf.lineString(coords)
            
            // Pivot point for rotation is the center of the line
            const pivot = [centerLng, centerLat]
            // Rotate using Turf.js (angle in degrees, positive clockwise)
            const rotated = turf.transformRotate(lineString, angleDegrees, { pivot })
            
            // Get rotated start and end to determine new bearing
            const rotatedCoords = rotated.geometry.coordinates.map(([lng, lat]) => 
                new mapStore.google.maps.LatLng(lat, lng)
            )
            const rotatedStart = rotatedCoords[0]
            const rotatedEnd = rotatedCoords[rotatedCoords.length - 1]
            const bearing = mapStore.google.maps.geometry.spherical.computeHeading(rotatedStart, rotatedEnd)
            
            // Adjust start and end symmetrically from center to maintain original length
            const adjustedStart = mapStore.google.maps.geometry.spherical.computeOffset(center, halfLength, bearing + 180)
            const adjustedEnd = mapStore.google.maps.geometry.spherical.computeOffset(center, halfLength, bearing)
            
            // Update coordinates and polyline
            line.coordinates = [adjustedStart, adjustedEnd]
            line.polyline.setPath([adjustedStart, adjustedEnd])
            this.isLineChanged = true
        },
        async downloadRoadLine() {
            if (!this.lines[1] || this.lines[1].coordinates.length < 2) {
                console.error('Road line not available')
                return
            }

            // Convert Google Maps coordinates to GeoJSON LineString format [lng, lat]
            const coords = this.lines[1].coordinates.map(coord => [coord.lng(), coord.lat()])
            
            const feature = {
                type: 'Feature',
                geometry: {
                    type: 'LineString',
                    coordinates: coords
                },
                properties: {
                    name: 'Road Line'
                }
            }

            const fileName = `road_line_${moment().format('YYYY-MM-DD-HH-mm')}`
            await downloadFeatureAsShapefile(feature, fileName)
        }
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useFieldsConnectionStore, import.meta.hot))
}
